// Shared contract between the public forms and their API routes. Imported by
// both client components and server routes, so it must stay free of
// 'server-only' and of any node-specific import.

/**
 * Name of the decoy field rendered on every public form.
 *
 * Deliberately NOT a real-sounding field name. The obvious choices (`company`,
 * `website`, `address`) are exactly what browser autofill and password
 * managers target, and Chrome ignores `autocomplete="off"` often enough that a
 * real visitor would eventually get their submission silently dropped. Losing
 * a genuine lead costs far more than missing one bot.
 *
 * A neutral internal-looking name is still caught by the spam bots that matter
 * here, because they fill every input in the form rather than only the fields
 * they recognise.
 */
export const HONEYPOT_FIELD = 'gp_ref_code';

/**
 * Timestamp field carrying when the form was rendered, as epoch ms.
 * Submissions that arrive faster than a human could type are rejected.
 */
export const RENDERED_AT_FIELD = 'renderedAt';

/** Fastest plausible human completion. Below this, treat as scripted. */
export const MIN_FILL_MS = 3_000;

/**
 * reCAPTCHA v3 action names. Verified server-side against the action Google
 * echoes back, so each token only works on the flow it was minted for.
 */
export const RECAPTCHA_ACTIONS = {
    contactForm: 'contact_form',
    itemInquiry: 'item_inquiry',
    viewContactInfo: 'view_contact_info',
    newsletterSignup: 'newsletter_signup',
} as const;

/**
 * True when a submission looks scripted on the cheap, local signals — decoy
 * field filled, or submitted implausibly fast.
 *
 * Checked before the reCAPTCHA round trip so obvious bots cost no outbound
 * request. Returns a reason for logging rather than a bare boolean.
 */
export function detectBotSignals(body: Record<string, unknown>): string | null {
    const decoy = body[HONEYPOT_FIELD];
    if (typeof decoy === 'string' && decoy.trim() !== '') return 'honeypot-filled';
    // A non-string, non-empty value means something scripted set it too.
    if (decoy != null && typeof decoy !== 'string') return 'honeypot-filled';

    const renderedAt = body[RENDERED_AT_FIELD];
    if (typeof renderedAt === 'number' && Number.isFinite(renderedAt)) {
        const elapsed = Date.now() - renderedAt;
        // Only reject implausibly *fast* fills. A clock-skewed client can
        // produce a negative or huge elapsed value; neither is evidence of a
        // bot, so both pass.
        if (elapsed >= 0 && elapsed < MIN_FILL_MS) return 'too-fast';
    }

    return null;
}

/** Strip guard fields so they are never forwarded to the email service. */
export function stripGuardFields<T extends Record<string, unknown>>(body: T): Partial<T> {
    const cleaned = { ...body };
    delete cleaned[HONEYPOT_FIELD];
    delete cleaned[RENDERED_AT_FIELD];
    delete (cleaned as Record<string, unknown>).recaptchaToken;
    return cleaned;
}
